/* eslint-disable @typescript-eslint/no-explicit-any */
import {AfterViewInit, Component, DestroyRef, NgZone, OnInit, ViewEncapsulation} from '@angular/core';
import {CommonModule, NgIf} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {WidgetService} from '../../services/widget.service';
import {MetaData, NgEventBus} from 'ng-event-bus';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {LookupService} from '../../../common/service/lookup.service';
import {WidgetConfigLookup} from '../../../common/models/widget-config.model';
import {GeometryTabComponent} from './tabs/geometry/geometry-tab.component';
import {SurveyTabComponent} from './tabs/survey/survey-tab.component';
import {CdkDrag, CdkDragHandle, DragRef, Point} from '@angular/cdk/drag-drop';
import {HELP_LINK_CONTENTS_BASE_URL} from '../../../common/constants';
import {HelpLinkComponent} from '../../../help/help-link.component';
import {NLR_TABS} from '../../../common/constants';

@Component({
    selector: 'nlr-attribute-viewer',
    imports: [CommonModule, FormsModule, GeometryTabComponent, SurveyTabComponent, CdkDrag, CdkDragHandle, HelpLinkComponent],
    providers: [CommonModule],
    templateUrl: './attribute-viewer.component.html',
    styleUrl: './attribute-viewer.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class AttributeViewerComponent implements OnInit, AfterViewInit {
    title = 'AttributeViewerComponent';

    widgetId = 'attributeViewer';

    widgetConfig: WidgetConfigLookup = {};

    open = true; // Used to open and close accordion on UI

    activeTab = 'geometryTab';

    disabledTabs = [];

    editorConfig = {
        show: false,
        style: {}
    };

    helpLinkUrl: string = HELP_LINK_CONTENTS_BASE_URL + 'attribute-viewer-help.html';

    helpPageTitle: string = 'Attribute Viewer';

    selectedTab: NLR_TABS;

    constructor(
        private destroyRef: DestroyRef,
        private eventBus: NgEventBus,
        private lookupService: LookupService,
        private widgetService: WidgetService,
        private ngZone: NgZone
    ) {}

    ngOnInit() {
        console.debug(`${this.title} - ngOnInit`);

        this.widgetConfig = this.lookupService.getLookups('widgetConfig').find((l: WidgetConfigLookup) => l.name === this.widgetId);

        this.eventBus
            .on('disableTab')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                const tab = meta.data;
                console.debug(`${this.title} - disableTab:`, tab);
                if (!this.disabledTabs.includes(tab)) {
                    this.disabledTabs.push(tab);
                }
            });

        this.eventBus
            .on('enableTab')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                const tab = meta.data;
                console.debug(`${this.title} - enableTab:`, tab);
                const tabIndex = this.disabledTabs.indexOf(tab);
                if (tabIndex !== -1) {
                    this.disabledTabs.splice(tabIndex, 1);
                }
            });

        this.eventBus
            .on('nlr:tabChange:*')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                this.selectedTab = meta.data;
            });
    }

    ngAfterViewInit() {
        this.widgetService.setupWidget(this.widgetId, () => this.eventBus.cast('attributeViewerResized'));
    }

    computeDragPosition = (userPointerPosition: Point, dragRef: DragRef, dimensions: DOMRect): Point => {
        return this.ngZone.runOutsideAngular(() => {
            return this.widgetService.computeDragPosition(userPointerPosition, dragRef, dimensions);
        });
    };

    showOrHide() {
        this.widgetService.showOrHideWidgetContent('attributeViewer', this.open);
        this.open = !this.open;
    }

    onClose() {
        console.debug(`${this.title} - onClose`);
        this.eventBus.cast('AttributeViewer:close');
        this.widgetService.onClick('AttributeViewer');
    }

    isDisabled() {
        return this.widgetService.disabledWidgets.includes('AttributeViewer');
    }

    isTabDisabled(tab: string) {
        return this.disabledTabs.includes(tab) || this.isDisabled();
    }

    onClickTab(tab: string) {
        console.debug(`${this.title} - onClick - tab: ${tab}`);

        if (this.isTabDisabled(tab)) {
            return;
        }

        this.activeTab = tab;
        this.eventBus.cast('filterFeatureSelection', false);
    }
}
